import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ParticipantBreakdownMatch } from '@/services/api';
import { isCompleted } from '@/constants/matchStatus';
import { useAuth } from '@/hooks/useAuth';
import { MainSection } from '@/components/layout/MainSection';
import ParticipantStatsHeader from '@/components/ParticipantStatsHeader';
import ParticipantHeroPoints from '@/components/ParticipantHeroPoints';
import ParticipantMatchRow from '@/components/ParticipantMatchRow';
import ParticipantStatsFilter, {
  type ParticipantStatsFilterValue,
} from '@/components/ParticipantStatsFilter';
import { useParticipantStatsContext } from '@/layouts/ParticipantStatsLayout/context';
import styles from './participantStats.module.scss';

/**
 * Статистика tab — merged single-page view for `/tournaments/:tid/leaderboard/:uid`.
 *
 * Scroll model:
 * - Mobile (`< lg`): `.scroll` is the bounded internal scroller (the page is
 *   height-bounded by SwipeableTabContent). The liga card scrolls away and the
 *   filter pins to the top; cards scroll underneath it.
 * - Desktop (`≥ lg`): the document scrolls (the app shell is not viewport-
 *   bounded on desktop), so `.scroll` is `overflow: visible` — otherwise a
 *   content-height `overflow:auto` container with `overscroll-behavior:contain`
 *   traps the wheel and the page can't scroll. The filter is static there.
 *
 * Layout responsibility:
 * - The accuracy stats card (`ParticipantStatsHeader`) is rendered by
 *   `ParticipantStatsLayout` in the desktop sidebar; `headerInSidebar` tells
 *   this tab to skip the inline copy on `≥ lg`. On mobile it renders inline.
 * - The filter + per-match breakdown (1-up mobile / 2-up grid desktop) render
 *   here. The filter dropdown is portaled so it isn't clipped by the section.
 *
 * The card title (h2) shows the participant's name (first + last, via the
 * layout's `displayName`), matching the design. The name appears nowhere else
 * in the content (the app top bar shows no title on this page), so the title
 * renders on every viewport — no hide flags.
 */
const StatsTab: React.FC = () => {
  const { data, headerInSidebar, displayName, distribution, userId } = useParticipantStatsContext();
  const { user } = useAuth();
  const { t } = useTranslation(['tournament']);
  const [filter, setFilter] = React.useState<ParticipantStatsFilterValue>('all');

  const filteredMatches = React.useMemo<ParticipantBreakdownMatch[]>(() => {
    if (filter === 'all') return data.matches;
    return data.matches.filter(m => {
      const breakdown = m.points_breakdown;
      const total = breakdown?.total_points ?? 0;
      const score = breakdown?.score_points ?? 0;
      const result = breakdown?.result_points ?? 0;
      const finished = isCompleted(m.status) && m.actual_score !== null;
      switch (filter) {
        case 'win':           return !!breakdown && total > 0;
        case 'loss':          return finished && !!breakdown && total === 0;
        case 'exactScore':    return !!breakdown && score > 0;
        case 'outcomeResult': return !!breakdown && result > 0;
        case 'pending':       return !finished;
        default:              return true;
      }
    });
  }, [data.matches, filter]);

  const { participant, matches } = data;
  const isOwn = String(user?.id) === userId;

  return (
    <MainSection
      fill
      title={displayName}
      headerRight={<ParticipantHeroPoints value={participant.total_points} />}
    >
      <div className={styles.scroll}>
        {!headerInSidebar && (
          <div className={styles.ligaSlot}>
            <ParticipantStatsHeader
              totalPredictions={participant.total_predictions}
              correctPredictions={participant.correct_predictions}
              totalPoints={participant.total_points}
              distribution={distribution}
            />
          </div>
        )}

        {/*
          Sticky-filter + results wrapper. On mobile its `min-height: 100%`
          keeps the scrollable area at least one viewport tall even when the
          filtered result set collapses to the short empty state — so the
          scroller never clamps scrollTop and the pinned filter (plus the
          scrolled-away accuracy block) stay exactly where the user left them
          instead of jumping back down. No-op on desktop (filter isn't sticky).
        */}
        <div className={styles.stickyWrap}>
          {matches.length > 0 && (
            <ParticipantStatsFilter value={filter} onChange={setFilter} />
          )}

          {matches.length === 0 ? (
            <div className={styles.statePad}>
              <div className={styles.emptyState}>
                <h3 className={styles.emptyTitle}>
                  {t('tournament:participantStats.emptyTitle')}
                </h3>
                <p className={styles.emptyDescription}>
                  {t('tournament:participantStats.emptyDescription')}
                </p>
              </div>
            </div>
          ) : filteredMatches.length === 0 ? (
            <div className={styles.statePad}>
              <div className={styles.emptyState}>
                <h3 className={styles.emptyTitle}>
                  {t('tournament:participantStats.filterEmptyTitle')}
                </h3>
                <p className={styles.emptyDescription}>
                  {t('tournament:participantStats.filterEmptyDescription')}
                </p>
              </div>
            </div>
          ) : (
            <div className={styles.list}>
              {filteredMatches.map(match => (
                <ParticipantMatchRow key={match.match_id} match={match} isOwn={isOwn} />
              ))}
            </div>
          )}
        </div>
      </div>
    </MainSection>
  );
};

export default StatsTab;
